import { CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { withdrawApplication } from '../lib/membership';

type Status = 'idle' | 'submitting' | 'withdrawn' | 'invalid' | 'error';

/**
 * Withdraw confirmation page (SC-007, FR-017/018). The emailed link lands here as a plain GET that only
 * renders a confirm button — deletion happens on an explicit POST, so email-client/scanner prefetch can
 * never delete a record. Shows only generic messages: NO applicant PII is fetched or rendered.
 */
export default function Withdraw() {
  const { token = '' } = useParams();
  const [status, setStatus] = useState<Status>('idle');
  const resultRef = useRef<HTMLDivElement>(null);

  async function confirm() {
    if (status === 'submitting') return;
    setStatus('submitting');
    const res = await withdrawApplication(token);
    const next: Status =
      res.kind === 'withdrawn'
        ? 'withdrawn'
        : res.kind === 'invalid'
          ? 'invalid'
          : res.kind === 'rate_limited' || res.kind === 'error'
            ? 'error'
            : 'error';
    setStatus(next);
    queueMicrotask(() => resultRef.current?.focus());
  }

  return (
    <div className="mx-auto w-full max-w-xl px-6 py-20 sm:py-28">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">
        Membership
      </p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">
        Withdraw your application
      </h1>

      {status === 'withdrawn' && (
        <div
          ref={resultRef}
          tabIndex={-1}
          className="mt-8 flex items-start gap-3 rounded-lg border border-border bg-surface p-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <CheckCircle2 aria-hidden className="mt-0.5 h-6 w-6 shrink-0 text-success" />
          <div>
            <p className="font-serif text-lg text-ink">Your application has been withdrawn.</p>
            <p className="mt-2 font-sans text-sm text-muted">
              Your details have been permanently removed. You’re always welcome to{' '}
              <Link to="/apply" className="text-accent underline underline-offset-2 hover:text-accent-deep">
                apply again
              </Link>{' '}
              in the future.
            </p>
          </div>
        </div>
      )}

      {(status === 'invalid' || status === 'error') && (
        <div
          ref={resultRef}
          tabIndex={-1}
          role="alert"
          className="mt-8 flex items-start gap-3 rounded-lg border border-error/40 bg-error/5 p-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <TriangleAlert aria-hidden className="mt-0.5 h-6 w-6 shrink-0 text-error" />
          <div>
            <p className="font-serif text-lg text-ink">
              {status === 'invalid'
                ? 'This link is invalid or has expired.'
                : 'Something went wrong. Please try again in a moment.'}
            </p>
            <p className="mt-2 font-sans text-sm text-muted">
              {status === 'invalid'
                ? 'The withdraw link may already have been used, or it’s more than 30 days old. If you still want to withdraw, please contact the board.'
                : 'If this keeps happening, please contact the board.'}
            </p>
          </div>
        </div>
      )}

      {(status === 'idle' || status === 'submitting') && (
        <>
          <p className="mt-4 max-w-prose font-serif text-lg leading-relaxed text-ink">
            This will permanently delete your pending application and the details you submitted. It
            can’t be undone, but you’re always welcome to apply again later.
          </p>
          <button
            type="button"
            onClick={confirm}
            disabled={status === 'submitting'}
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-md border border-error bg-error px-5 py-2.5 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error disabled:cursor-not-allowed disabled:opacity-60"
          >
            {status === 'submitting' && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
            {status === 'submitting' ? 'Withdrawing…' : 'Withdraw my application'}
          </button>
          <p className="sr-only" aria-live="polite">
            {status === 'submitting' ? 'Withdrawing your application.' : ''}
          </p>
        </>
      )}
    </div>
  );
}
